import {
  Path,
  ReferenceDirection,
  type DirectedTriple,
  type PathCollapsingRule,
  DirectedTripleWithFiltersWithGraphIds,
  TripleDirection,
  PathCollapsingRuleNonNullablePath,
  DirectedTripleWithFilters,
  CollapsingPathRuleMatch,
  PathMatch,
  ComponentPath,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import type {
  HierarchyOfLinkedComponentsWithDirection,
  HierarchyDefinition,
} from '@ardoq/data-model';
import { MetaModelViewGraphNode, MetamodelViewGraphEdge } from '@ardoq/graph';
import { logError } from '@ardoq/logging';
import { countBy, groupBy, last, uniqBy } from 'lodash';
import { getId } from 'viewpointBuilder/getId';
import { pathToKey } from 'viewpointBuilder/traversals/pathToKey';
import {
  NamedDirectedTriple,
  TraversalBuilderState,
  TripleOption,
  TripleOptionReplacement,
  VirtualEdgeState,
} from 'viewpointBuilder/types';

export const hasGraphIds = (
  triple: DirectedTriple
): triple is DirectedTripleWithFiltersWithGraphIds =>
  'sourceGraphId' in triple && 'targetGraphId' in triple;

export const getLinkedType = <T extends DirectedTriple>(triple: T): string =>
  triple.direction === 'outgoing' ? triple.targetType : triple.sourceType;

/**
 * Transform the paths to a hierarchy (or tree). We try to find For each path
 * in the already built hierarchy. Starting at the bottom, as long as we
 * find the subpath, we  ensure that the parent connections contains the link to
 * the found component. The rest of the subpath we don't find, we add to the
 * hierarchy.
 */
export const pathsToHierarchy = (
  pathsDict: Record<string, ComponentPath[]>
): Record<string, HierarchyOfLinkedComponentsWithDirection> => {
  const tuples: [string, HierarchyOfLinkedComponentsWithDirection][] =
    Object.entries(pathsDict).map(([id, paths]) => {
      const root: HierarchyOfLinkedComponentsWithDirection = {
        componentId: id,
        linkedComponents: [],
        parentConnections: [],
      };

      paths.forEach(path => {
        let current = root;
        path.forEach(
          ({ componentId, referenceId, direction, isParentChildReference }) => {
            const connection = current.linkedComponents.find(
              ({ componentId: componentIdCandidate }) =>
                componentIdCandidate === componentId
            );
            if (connection) {
              if (
                !connection.parentConnections.some(
                  ({
                    referenceId: candidateReferenceId,
                    direction: candidateDirection,
                  }) =>
                    candidateReferenceId === referenceId &&
                    candidateDirection === direction
                )
              ) {
                connection.parentConnections.push({
                  referenceId,
                  direction,
                  isParentChildReference,
                });
              }
              current = connection;
            } else {
              const newConnection: HierarchyOfLinkedComponentsWithDirection = {
                componentId,
                linkedComponents: [],
                parentConnections: [
                  { referenceId, direction, isParentChildReference },
                ],
              };
              current.linkedComponents.push(newConnection);
              current = newConnection;
            }
          }
        );
      });
      return [id, root];
    });

  return Object.fromEntries(
    // Only return components with linked components.
    tuples.filter(([, root]) => root.linkedComponents.length > 0)
  );
};

export const getMatchingPaths = <T extends DirectedTriple>(
  paths: T[][] | null,
  rule: PathCollapsingRuleNonNullablePath,
  /**
   * If used to build the instance hierarchy, include matches from any path
   * index to ensure all possible matches across all paths are captured.
   * If used to collapse type paths in the viewpoint builder, only one match is
   * needed to avoid duplicated collapsed paths.
   */
  includePathIndexInEqualityCheck = true,
  withMatchesOverlapValidation = true
): CollapsingPathRuleMatch<T>[] => {
  if (!paths) return [];
  const { path: rulePath } = rule;
  const invertedRulePath = invertTriplePath(rulePath);
  const isSymmetric = invertedRulePath.every((triple, index) =>
    isSameTriple(triple, rulePath[index])
  );
  const allMatchingPaths = paths.flatMap((path, pathIndex) => {
    const matches = isSymmetric
      ? getMatchesForPath(path, rulePath, 'start')
      : [
          ...getMatchesForPath(path, rulePath, 'start'),
          ...getMatchesForPath(path, invertedRulePath, 'end'),
        ];
    if (
      withMatchesOverlapValidation &&
      !validateMatchesAgainstOverlaps(matches)
    ) {
      logError(
        Error('Overlapping matches found for path in getMatchesForPath')
      );
      return [];
    }
    return matches.map(match => ({
      path,
      match,
      rule,
      pathIndex,
      matchKey: getMatchKey(match, pathIndex, includePathIndexInEqualityCheck),
    }));
  });
  const countMatchesIndex = countBy(allMatchingPaths, 'matchKey');
  allMatchingPaths.forEach(match => {
    match.match.matchCount = countMatchesIndex[match.matchKey];
  });
  return uniqBy(allMatchingPaths, 'matchKey');
};

const getMatchKey = (
  { rulePathKey, startIndex, start }: PathMatch,
  pathIndex: number,
  includePathIndexInEqualityCheck: boolean
) =>
  includePathIndexInEqualityCheck
    ? `${rulePathKey}-${startIndex}-${start}-${pathIndex}`
    : `${rulePathKey}-${startIndex}-${start}`;

export const isSameTriple = (
  triple1: DirectedTriple,
  triple2: DirectedTriple
) => {
  return (
    triple1 &&
    triple2 &&
    triple1.sourceType === triple2.sourceType &&
    triple1.targetType === triple2.targetType &&
    triple1.referenceType === triple2.referenceType &&
    triple1.direction === triple2.direction
  );
};

export const invertTriplePath = (path: DirectedTriple[]): DirectedTriple[] =>
  path
    .toReversed()
    .map(({ sourceType, targetType, referenceType, direction }) => ({
      sourceType,
      targetType,
      referenceType,
      direction: direction === 'outgoing' ? 'incoming' : 'outgoing',
    }));

/**
 * The direction a collapsing path is selected in defines the direction of the
 * reference that replaces it. A traversal writes its type paths in whatever
 * orientation it runs them, though, so a rule is matched both as selected and
 * inverted - otherwise it would not find the paths it was drawn over at all. A
 * match found inverted replaces the selection with a reference pointing the
 * other way (`start` is outgoing, `end` incoming).
 *
 * Each rule therefore offers every region it covers in one orientation or the
 * other, while a region can only be collapsed once. A region matched by any rule
 * as selected goes to the first such rule. A region reachable only inverted goes
 * to the first rule that won nothing as selected - which is what lets a second
 * rule over the same path name the opposite direction, and keeps one rule from
 * lending its name to a direction another rule was written for. With no such
 * rule the region stays with the first candidate, so a lone rule still collapses
 * both directions.
 *
 * Resolved in two passes over all the regions rather than region by region: a
 * rule's claim on the inverted regions depends on what it won as selected
 * anywhere, so a single pass would make the outcome depend on the order the
 * traversal happens to list its paths.
 */
export const assignRegionsToRules = <
  T extends {
    rule: { referenceTypeId: string };
    match: { start: 'start' | 'end'; startIndex: number; length: number };
    pathIndex: number;
  },
>(
  matches: T[]
): T[] => {
  const regionOf = ({ pathIndex, match }: T) =>
    `${pathIndex}:${match.startIndex}:${match.length}`;
  const regions = Object.values(groupBy(matches, regionOf));
  const winners = new Set<T>();
  const wonAsDrawn = new Set<string>();

  regions.forEach(candidates => {
    const asDrawn = candidates.find(({ match }) => match.start === 'start');
    if (asDrawn) {
      winners.add(asDrawn);
      wonAsDrawn.add(asDrawn.rule.referenceTypeId);
    }
  });

  regions
    .filter(candidates => !candidates.some(candidate => winners.has(candidate)))
    .forEach(candidates => {
      const unrewarded = candidates.find(
        ({ rule }) => !wonAsDrawn.has(rule.referenceTypeId)
      );
      winners.add(unrewarded ?? candidates[0]);
    });

  return matches.filter(match => winners.has(match));
};

export const getMatchesForPath = (
  path: DirectedTriple[],
  rulePath: DirectedTriple[],
  start: 'start' | 'end'
): PathMatch[] => {
  const firstRuleTriple = rulePath[0];
  // If a path has repeating triples we must try for each of them.
  const startIndexes = path
    .map((triple, index) =>
      isSameTriple(triple, firstRuleTriple) ? index : -1
    )
    .filter(index => index !== -1);
  if (startIndexes.length === 0) return [];
  return startIndexes
    .map(startIndex => {
      const isMatch = rulePath.every((ruleTriple, index) => {
        const pathTriple = path[startIndex + index];
        return isSameTriple(pathTriple, ruleTriple);
      });
      return isMatch
        ? {
            startIndex,
            length: rulePath.length,
            start,
            rulePathKey: pathToKey(rulePath),
            // Actual match count is set after all matches have been processed.
            matchCount: 0,
          }
        : null;
    })
    .filter(match => match !== null);
};

// When several matches share the same end position they are necessarily nested:
// an equal end index with a greater length implies a smaller start index, hence
// containment. We keep the longest match of each group, which subsumes the
// shorter, shadowed ones. This lets a user define one collapsing rule per path
// length (e.g. A→A→B, A→A→A→B, A→A→A→A→B) all collapsing to the same root→leaf
// edge, even though the shorter rule patterns also match the trailing part of
// the longer paths.
//
// Only nested same-end overlaps are resolved here. Genuine "crossing" overlaps
// (two matches with different end positions where neither contains the other)
// are left in place and rejected by validateMatchesAgainstOverlaps downstream.
export const keepLongestMatchPerEndPosition = <T extends { match: PathMatch }>(
  matches: T[]
): T[] => {
  const matchesByEndPosition = groupBy(
    matches,
    ({ match }) => match.startIndex + match.length
  );
  return Object.values(matchesByEndPosition).map(matchesAtEndPosition =>
    // Strict `>` means the first match in iteration order wins on a tie. A tie
    // (same end, same length) can only happen when two matches cover the exact
    // same region, i.e. duplicate/coinciding rules — the collapse is identical
    // either way, so it only affects which rule's virtual-edge label/style is
    // shown. First-defined rule wins, deterministically.
    matchesAtEndPosition.reduce((longest, candidate) =>
      candidate.match.length > longest.match.length ? candidate : longest
    )
  );
};

export const validateMatchesAgainstOverlaps = (matches: PathMatch[]) => {
  const matchesSortedByStartIndex = matches.toSorted(
    (a, b) => a.startIndex - b.startIndex
  );
  return matchesSortedByStartIndex.every((match, index) => {
    if (index === 0) return true;
    const previousMatch = matchesSortedByStartIndex[index - 1];
    // Matches cannot overlap.
    return match.startIndex >= previousMatch.startIndex + previousMatch.length;
  });
};

export const processMatches = (
  state: TraversalBuilderState,
  matches: CollapsingPathRuleMatch<DirectedTripleWithFiltersWithGraphIds>[]
): VirtualEdgeState[] => {
  const newEdges = matches.map(match => {
    const {
      path,
      match: { start, startIndex, length, matchCount },
      rule,
    } = match;

    const collapsedPath = path.slice(startIndex, startIndex + length);
    const { sourceGraphNodeId, targetGraphNodeId } =
      start === 'start'
        ? getSourceAndTargetGraphNodeIdFromStart(collapsedPath)
        : getSourceAndTargetGraphNodeIdFromEnd(collapsedPath);
    const collapsedEdges = collapsedPath.map(
      ({ referenceGraphId }) => referenceGraphId
    );
    const virtualEdge = MetamodelViewGraphEdge.create(
      // This is the model id used to look up the reference in the according
      // scope data. A virtual edge is not based on a reference, so this id
      // is not useful for anything. We need to make sure that this case is
      // handled in the according code branches. The main use case is building
      // the triple options, or rather creating nodes and edges from a selected
      // triples. For a virtual edge we need to make sure that the according
      // triple is disabled.
      `virtual-edge-${getId()}`,
      sourceGraphNodeId,
      targetGraphNodeId,
      state.graphNodes,
      getId(),
      {
        representationData: {
          ...rule.referenceStyle,
          displayLabel: rule.displayText,
        },
      }
    );
    virtualEdge.setIsVirtual(true);
    const startTriple = collapsedPath[0]!;
    const endTriple = last(collapsedPath)!;
    const replacementDirection = start === 'start' ? 'outgoing' : 'incoming';
    const tripleReplacements = getTripleReplacements(
      state,
      startTriple,
      endTriple,
      replacementDirection,
      virtualEdge
    );
    return {
      collapsedEdges,
      virtualEdge,
      // Can only be calculated properly after all matches have been processed.
      // See `editTraversalOperations.updateEdgesToBeRemovedOnCollapsing`
      edgesToBeRemovedOnCollapsing: [],
      tripleReplacements,
      matchCount,
    };
  });
  return newEdges;
};

const getTripleReplacements = (
  state: TraversalBuilderState,
  startTriple: DirectedTripleWithFiltersWithGraphIds,
  endTriple: DirectedTripleWithFiltersWithGraphIds,
  replacementDirection: TripleDirection,
  virtualEdge: MetamodelViewGraphEdge
): TripleOptionReplacement[] => {
  const { graphNodes } = state;
  const { source, target } = virtualEdge;
  const sourceGraphNode = graphNodes.get(source);
  const targetGraphNode = graphNodes.get(target);
  if (!(sourceGraphNode && targetGraphNode)) {
    logError(
      Error('Missing source or target graph node in getTripleReplacements')
    );
    return [];
  }

  return [
    getStartTripleReplacement({
      state,
      triple: startTriple,
      replacementDirection,
      sourceGraphNode,
      targetGraphNode,
      virtualEdge,
    }),
    getEndTripleReplacement({
      state,
      triple: endTriple,
      replacementDirection:
        replacementDirection === 'outgoing' ? 'incoming' : 'outgoing',
      sourceGraphNode,
      targetGraphNode,
      virtualEdge,
    }),
  ].filter(isTruthy);
};

type GetTripleReplacement = {
  state: TraversalBuilderState;
  replacementDirection: TripleDirection;
  sourceGraphNode: MetaModelViewGraphNode;
  targetGraphNode: MetaModelViewGraphNode;
  virtualEdge: MetamodelViewGraphEdge;
  triple: DirectedTripleWithFiltersWithGraphIds;
};

const getStartTripleReplacement = (
  args: GetTripleReplacement
): TripleOptionReplacement | null => getTripleReplacement(args, 'start');

const getEndTripleReplacement = (
  args: GetTripleReplacement
): TripleOptionReplacement | null => getTripleReplacement(args, 'end');

const getTripleReplacement = (
  {
    state,
    triple,
    replacementDirection,
    sourceGraphNode,
    targetGraphNode,
    virtualEdge,
  }: GetTripleReplacement,
  start: 'start' | 'end'
): TripleOptionReplacement | null => {
  const { graphNodes, graphEdges } = state;
  const { sourceGraphId, targetGraphId, referenceGraphId } = triple;
  const collapsedSource = graphNodes.get(sourceGraphId);
  const collapsedTarget = graphNodes.get(targetGraphId);
  const collapsedReference = graphEdges.get(referenceGraphId);
  const virtualTripleOption = getVirtualTripleOption({
    state,
    sourceGraphNode,
    targetGraphNode,
    virtualEdge,
    context:
      replacementDirection === 'outgoing' ? sourceGraphNode : targetGraphNode,
  });
  if (
    !(
      collapsedSource &&
      collapsedTarget &&
      collapsedReference &&
      virtualTripleOption
    )
  ) {
    logError(
      Error(
        'Missing source, target or reference graph node in getStartTripleReplacement'
      )
    );
    return null;
  }
  const collapsedTripleId = collapsedReference.modelId;

  return {
    start,
    graphNodeId: getGraphNodeIdFromStartAndDirection(
      start,
      triple.direction,
      collapsedReference
    ),
    tripleId: collapsedTripleId,
    direction: triple.direction,
    replacementDirection,
    tripleReplacement: virtualTripleOption,
  };
};

const getGraphNodeIdFromStartAndDirection = (
  start: 'start' | 'end',
  tripleDirection: TripleDirection,
  collapsedReference: MetamodelViewGraphEdge
) => {
  if (start === 'start') {
    return tripleDirection === 'outgoing'
      ? collapsedReference.source
      : collapsedReference.target;
  }

  return tripleDirection === 'outgoing'
    ? collapsedReference.target
    : collapsedReference.source;
};

type GetVirtualOptionArgs = {
  state: TraversalBuilderState;
  sourceGraphNode: MetaModelViewGraphNode;
  targetGraphNode: MetaModelViewGraphNode;
  virtualEdge: MetamodelViewGraphEdge;
  context: MetaModelViewGraphNode;
};

const getVirtualTripleOption = ({
  state,
  sourceGraphNode,
  targetGraphNode,
  virtualEdge,
  context,
}: GetVirtualOptionArgs): TripleOption | null => {
  const { instanceCounts } = state;
  const tripleId = virtualEdge.modelId;
  const isSelected = true;
  const isDisabled = true;
  const togglingDisabledExplanation = `This is a collapsed path. It cannot be unselected.
The collapsing rules can be changed in the 'Collapse paths' tab.`;
  const metaDataComponent =
    context === sourceGraphNode
      ? targetGraphNode.metaData
      : sourceGraphNode.metaData;
  const metaDataReference = virtualEdge.getMetadata();
  const namedDirectedTriple: NamedDirectedTriple = {
    // The direction doesn't really matter for a virtual edge. They are not
    // selectable by the user and never really part of the traversal definition.
    // (the direction indicates in which direction the triple should be traversed)
    direction: ReferenceDirection.OUTGOING,
    sourceType: sourceGraphNode.getLabel(),
    referenceType: virtualEdge.getLabel(),
    targetType: targetGraphNode.getLabel(),
  };
  const countKey = '';
  const tripleInstanceCount =
    instanceCounts.get(targetGraphNode.id)?.counts ?? null;
  return {
    tripleId,
    isSelected,
    isDisabled,
    togglingDisabledExplanation,
    metaDataComponent,
    metaDataReference,
    namedDirectedTriple,
    countKey,
    instanceCounts: tripleInstanceCount,
  };
};

const getSourceAndTargetGraphNodeIdFromStart = (
  path: DirectedTripleWithFiltersWithGraphIds[]
) => {
  const startTriple = path[0];
  const endTriple = path[path.length - 1];
  const sourceGraphNodeId =
    startTriple.direction === 'outgoing'
      ? startTriple.sourceGraphId
      : startTriple.targetGraphId;
  const targetGraphNodeId =
    endTriple.direction === 'outgoing'
      ? endTriple.targetGraphId
      : endTriple.sourceGraphId;
  return { sourceGraphNodeId, targetGraphNodeId };
};

const getSourceAndTargetGraphNodeIdFromEnd = (
  path: DirectedTripleWithFiltersWithGraphIds[]
) => {
  const startTriple = path[path.length - 1];
  const endTriple = path[0];
  const sourceGraphNodeId =
    startTriple.direction === 'outgoing'
      ? startTriple.targetGraphId
      : startTriple.sourceGraphId;
  const targetGraphNodeId =
    endTriple.direction === 'outgoing'
      ? endTriple.sourceGraphId
      : endTriple.targetGraphId;
  return { sourceGraphNodeId, targetGraphNodeId };
};

export const toCollapsedPaths = (
  paths: Path[],
  pathCollapsingRules: PathCollapsingRuleNonNullablePath[]
): Path[] => {
  const matchesPerPathIndex = groupBy(
    assignRegionsToRules(
      pathCollapsingRules.flatMap(rule => getMatchingPaths(paths, rule))
    ),
    'pathIndex'
  );

  return paths.map((path, pathIndex) => {
    // Resolve nested same-end overlaps (keep the longest match), mirroring the
    // logic used when the rules are actually applied, so the displayed collapsed
    // type path matches the resulting traversal.
    const matches = keepLongestMatchPerEndPosition(
      matchesPerPathIndex[pathIndex] ?? []
    ).toSorted((a, b) => b.match.startIndex - a.match.startIndex);
    if (!validateCollapsingRuleMatches(matches)) {
      logError(Error('Overlapping matches found for path in toCollapsedPaths'));
      return path;
    }
    return matches.reduce((collapsedPath, match) => {
      return [
        ...collapsedPath.slice(0, match.match.startIndex),
        getCollapsedTriple(match),
        ...collapsedPath.slice(match.match.startIndex + match.match.length),
      ];
    }, path);
  });
};

const validateCollapsingRuleMatches = <T extends DirectedTripleWithFilters>(
  matches: CollapsingPathRuleMatch<T>[]
) =>
  matches.every(({ match: { startIndex, length } }, index) => {
    if (index === 0) return true;
    const previousMatch = matches[index - 1];
    return startIndex + length <= previousMatch.match.startIndex;
  });

export const getCollapsedTriple = (matchObject: {
  path: DirectedTripleWithFilters[];
  match: {
    startIndex: number;
    length: number;
    start: string;
  };
  rule: PathCollapsingRule & { path: DirectedTriple[] };
  pathIndex: number;
}): DirectedTripleWithFilters => {
  const firstTriple = matchObject.rule.path[0];
  const lastTriple = last(matchObject.rule.path)!;
  const referenceType = matchObject.rule.displayText;
  const sourceType =
    firstTriple.direction === 'outgoing'
      ? firstTriple.sourceType
      : firstTriple.targetType;
  const targetType =
    lastTriple.direction === 'outgoing'
      ? lastTriple.targetType
      : lastTriple.sourceType;
  const direction =
    matchObject.match.start === 'start' ? 'outgoing' : 'incoming';

  return {
    sourceType,
    targetType,
    referenceType,
    direction,
    isVirtual: true,
    referenceTypeId: matchObject.rule.referenceTypeId,
  };
};

export const toPathDictionary = (
  hierarchyDefinition: HierarchyDefinition[]
) => {
  const result: Record<string, ComponentPath[]> = {};
  for (const { pathResults } of hierarchyDefinition) {
    for (const componentPaths of pathResults) {
      for (const componentPath of componentPaths) {
        if (componentPath.length === 0) continue;
        const [{ componentId }, ...tail] = componentPath;
        if (!result[componentId]) {
          result[componentId] = [];
        }
        result[componentId].push(tail);
      }
    }
  }

  return result;
};
